import React from 'react';
import PropTypes from 'prop-types';

import { LISTING_IMAGE_PLACEHOLDER_URL } from 'common/listingConstants';

import { getListingManagedPhotoUrls } from 'common/listingHelpers';

import { DATA_TABLE_SOURCE_SUNSETUP_WITHDRAWNS } from 'common/dataTableConstants';

import SrText from 'client/components/SrText';
import SrPartnerList from 'client/components/SrPartnerList';
import SrPartnerSection from 'client/components/SrPartnerSection';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrPartnerListingCard, {
  SR_PARTNER_LISTING_CARD_TAG_THEME_ORANGE,
  SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE_GREY_SUNSCREEN,
  SR_PARTNER_LISTING_CARD_TAG_THEME_RED_SUNSCREEN,
} from 'client/components/SrPartnerListingCard';

import { getLastUninstallJobOrder } from 'common/marketingPartnerListingHelpers';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import { JOB_ORDER_STATUS_COMPLETED, JOB_ORDER_STATUS_FAILED } from 'common/jobConstants';

const JOB_ORDER_STATUS_TAG_MAPPING = {
  [JOB_ORDER_STATUS_COMPLETED]: (jobDate) => ({
    theme: SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE_GREY_SUNSCREEN,
    label: momentWithAustinTimezone(jobDate).format('MMM D, YYYY'),
  }),
  [JOB_ORDER_STATUS_FAILED]: (jobDate) => ({
    theme: SR_PARTNER_LISTING_CARD_TAG_THEME_RED_SUNSCREEN,
    label: `Uninstall failed ${momentWithAustinTimezone(jobDate).format('MMM D, YYYY')}`,
  }),
};
const JOB_ORDER_STATUS_TEXT_MAPPING = {
  [JOB_ORDER_STATUS_COMPLETED]: (jobDate) =>
    `Uninstalled ${momentWithAustinTimezone(jobDate).format('MMM D, YYYY')}`,
  [JOB_ORDER_STATUS_FAILED]: (jobDate) =>
    `Uninstall failed ${momentWithAustinTimezone(jobDate).format('MMM D, YYYY')}`,
};

const SrDataTableUninstallsPartnerListings = ({ partner, apartment }) => {
  if (partner == null) return null;

  const dataTableSettings = {
    itemsPropName: 'listings',
    dataSource: DATA_TABLE_SOURCE_SUNSETUP_WITHDRAWNS,
    rootViewId: 'DATA_TABLE_SOURCE_SUNSETUP_WITHDRAWNS',
    initialViewSettings: {
      viewId: 'DATA_TABLE_SOURCE_SUNSETUP_WITHDRAWNS',
      loadAllData: true,
      filter: {
        'listings.partnerId': partner.id,
      },
    },
  };

  const uninstallJobIds = [
    'yardSignTakedownJob',
    'comboLockTakedownJob',
    'sunlockTakedownJob',
    'selfShowingHardwareTakedownJob',
  ];

  const getTitle = (listing) => {
    const isUnit = !!apartment;
    let title = listing.addressStreetDisplay;

    if (isUnit) {
      const apartmentUnit = listing.apartmentUnit || '';
      title = apartmentUnit.toLowerCase().includes('unit') ? (
        apartmentUnit
      ) : (
        <span>
          <span>{`Unit ${apartmentUnit || '--'} `}</span>
          {listing.floorPlanName && (
            <span className="floorPlanName">{`(${listing.floorPlanName})`}</span>
          )}
        </span>
      );
    }
    return title;
  };

  const getStats = (lastUninstallJobOrder) => {
    let stat =
      'Uninstall ordered ' +
      momentWithAustinTimezone(lastUninstallJobOrder.createdDate).format('MMM D');
    const statText = JOB_ORDER_STATUS_TEXT_MAPPING[lastUninstallJobOrder.jobOrderStatus];

    if (statText != null) {
      stat = statText(lastUninstallJobOrder.jobOrderCompletedDate);
    }

    return [stat];
  };

  const getTag = (lastUninstallJobOrder) => {
    let tag = {
      theme: SR_PARTNER_LISTING_CARD_TAG_THEME_ORANGE,
      label: 'Uninstall in progress',
    };
    const tagTheme = JOB_ORDER_STATUS_TAG_MAPPING[lastUninstallJobOrder.jobOrderStatus];

    if (tagTheme != null) {
      tag = tagTheme(lastUninstallJobOrder.jobOrderCompletedDate);
    }

    return [tag];
  };

  return (
    <SrDataTableConnected {...dataTableSettings}>
      {({ listings, dataTableApiState, dataTableIsFullyLoaded }) => {
        const noItems = dataTableIsFullyLoaded && listings.length === 0;
        return (
          <div>
            <SrPartnerSection title="Withdrawn Listings">
              {noItems && (
                <SrText font="greycliff" weight="600" color="grey" fontSize="16px">
                  There are no withdrawn listings right now.
                </SrText>
              )}
              <SrPartnerList singleRow>
                {listings.map((listing) => {
                  const lastUninstallJobOrder =
                    getLastUninstallJobOrder({
                      marketingPartnerListing: listing,
                      uninstallJobIds,
                    }) || {};
                  const [listingPhoto] = getListingManagedPhotoUrls({
                    listing: listing,
                  });
                  const title = getTitle(listing);
                  const stats = getStats(lastUninstallJobOrder);
                  const tag = getTag(lastUninstallJobOrder);

                  return (
                    <SrPartnerListingCard
                      key={`setup-completed-${listing.id}`}
                      to={`/partner/${partner.slug}/listing/${listing.id}`}
                      photos={[listingPhoto || LISTING_IMAGE_PLACEHOLDER_URL]}
                      title={title}
                      stats={stats}
                      tags={tag}
                    />
                  );
                })}
              </SrPartnerList>
              {dataTableApiState.inProgress && <div className="loading-ellipses" />}
            </SrPartnerSection>
          </div>
        );
      }}
    </SrDataTableConnected>
  );
};

SrDataTableUninstallsPartnerListings.propTypes = {
  partner: PropTypes.object.isRequired,
  apartment: PropTypes.object,
};

SrDataTableUninstallsPartnerListings.displayName = 'SrDataTableUninstallsPartnerListings';

export default SrDataTableUninstallsPartnerListings;
